import { Fragment, useEffect, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { Divider } from 'react-native-elements';

import { fetchTherapistTags } from '@/api/services/therapistTags';
import { Text } from '@/components/Text';
import { FilterDrawer } from '@/components/ui/therapist/FilterDrawer';
import { Input } from '@/components/ui/therapist/Input';
import { Tag } from '@/components/ui/therapist/Tag';
import colors from '@/config/colors';
import type { TherapistTag } from '@/stores/store.types';
import { logError } from '@/utils/logHandling';

import { styles } from './patientFilter.styles';

type PatientFilterProps = {
  isOpen: boolean;
  onClose: () => void;
  drawerWidth?: number;
  resetFilter: () => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  tagFilter: string[];
  setTagFilter: (tagId: string, isSelected: boolean) => void;
  applyFilter: () => void;
};

export const PatientFilter = ({
  isOpen,
  onClose,
  drawerWidth,
  resetFilter,
  searchTerm,
  setSearchTerm,
  tagFilter,
  setTagFilter,
  applyFilter,
}: PatientFilterProps) => {
  const [availableTags, setAvailableTags] = useState<TherapistTag[]>([]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const loadTherapistTags = async () => {
      try {
        const tags = await fetchTherapistTags();
        setAvailableTags(tags);
      } catch (error) {
        logError('Could not fetch therapist tags for patient filter', error);
      }
    };

    loadTherapistTags();
  }, [isOpen]);

  return (
    <FilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onApply={applyFilter}
      onReset={resetFilter}
      drawerWidth={drawerWidth}
    >
      <View style={styles.section}>
        <Input
          label="Patient:in suchen"
          value={searchTerm}
          onChange={setSearchTerm}
          iconLeft="magnifyingGlass"
          placeholder="Patient:in suchen"
        />

        <View style={styles.dividerSpacing}>
          <Divider width={1} color={colors.newUI.therapist.primaryBlue10} />
        </View>
      </View>

      {availableTags.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.categoryLabel}>Patient:in filtern</Text>

          <View style={styles.tagListContainer}>
            <ScrollView
              nestedScrollEnabled
              showsVerticalScrollIndicator
              contentContainerStyle={styles.tagList}
            >
              {availableTags.map((tag, index) => {
                const isSelected = tagFilter.includes(tag._id);

                return (
                  <Fragment key={tag._id}>
                    {index > 0 && (
                      <Divider
                        width={1}
                        color={colors.newUI.therapist.primaryBlue10}
                      />
                    )}

                    <Pressable
                      onPress={() => setTagFilter(tag._id, !isSelected)}
                      style={styles.tagRow}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: isSelected }}
                    >
                      <Tag
                        label={tag.name}
                        isSelected={isSelected}
                        style={styles.tag}
                        labelStyle={styles.tagLabel}
                      />
                    </Pressable>
                  </Fragment>
                );
              })}
            </ScrollView>
          </View>
        </View>
      )}
    </FilterDrawer>
  );
};
